import TypeUtils from 'utility/Type/TypeUtils';

function createLazyGetter<T>(getter: () => T): () => T {
    let value: T;
    return function getLazyValue() {
        if (value) {
            return value;
        }
        value = getter();
        return value;
    };
}

const getWeekdays = (startIndex = 0) => {
    const weekdays = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    if (startIndex === 0) {
        return weekdays;
    }
    const numberOfWeekdays = weekdays.length;
    const indexedWeekdays = [];
    for (let i = 0; i < numberOfWeekdays; i++) {
        indexedWeekdays.push(weekdays[(startIndex + i) % numberOfWeekdays]);
    }
    return indexedWeekdays;
};

export enum DateFormat {
    Day = 'd',
    Short = 'd.m.',
    Full = 'd.m.Y',
    FullTime = 'd.m.Y H:i',
    Weekday = 'D',
    WeekdayDayMonth = 'D d.m',
    WeekdayCommaDayMonth = 'D, d.m',
    WeekdayDayMonthYear = 'D, d.m.Y',
    WeekdayDayMonthShortYear = 'D, d.m.y',
    WeekdayDayMonthWithoutYear = 'D, d.m.',
    WeekdayDayLongMonthLongYear = 'D, {den} d. M y',
    FullShortYear = 'd.m.y',
    SpsExpiryDate = 'd/m/Y',
    LongMonthYear = 'M Y',
    FormatYMD = 'Y-m-d',
    HourMinute = 'H:i',
}

enum DateFormatter {
    Weekday = 'Weekday',
    LongWeekday = 'LongWeekday',
    Day = 'Day',
    Month = 'Month',
    LongMonth = 'LongMonth',
    Year = 'Year',
    ShortYear = 'ShortYear',
    HourMinute = 'HourMinute',
    Time = 'Time',
}

const GermanMonths = [
    'Januar',
    'Februar',
    'März',
    'April',
    'Mai',
    'Juni',
    'Juli',
    'August',
    'September',
    'Oktober',
    'November',
    'Dezember',
];

const lazyDateFormatters = createLazyGetter<
    Record<DateFormatter, Intl.DateTimeFormat>
>(() => {
    return {
        [DateFormatter.Weekday]: new Intl.DateTimeFormat('de-DE', {
            weekday: 'short',
        }),
        [DateFormatter.LongWeekday]: new Intl.DateTimeFormat('de-DE', {
            weekday: 'long',
        }),
        [DateFormatter.Day]: new Intl.DateTimeFormat('de-DE', {
            day: '2-digit',
        }),
        [DateFormatter.Month]: new Intl.DateTimeFormat('de-DE', {
            month: '2-digit',
        }),
        [DateFormatter.LongMonth]: new Intl.DateTimeFormat('de-DE', {
            month: 'long',
        }),
        [DateFormatter.Year]: new Intl.DateTimeFormat('de-DE', {
            year: 'numeric',
        }),
        [DateFormatter.ShortYear]: new Intl.DateTimeFormat('de-DE', {
            year: '2-digit',
        }),
        [DateFormatter.Time]: new Intl.DateTimeFormat('de-DE', {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'Europe/Berlin',
        }),
        [DateFormatter.HourMinute]: new Intl.DateTimeFormat('de-DE', {
            hour: '2-digit',
            minute: '2-digit',
        }),
    };
});

function formatDateObject(date: Date, format: DateFormat = DateFormat.Full) {
    const formatters = lazyDateFormatters();
    const weekday = formatters[DateFormatter.Weekday].format(date);
    const day = formatters[DateFormatter.Day].format(date);
    const month = formatters[DateFormatter.Month].format(date);
    const year = formatters[DateFormatter.Year].format(date);

    switch (format) {
        case DateFormat.Day:
            return day;
        case DateFormat.Short:
            return `${day}.${month}.`;
        case DateFormat.Full:
            return `${day}.${month}.${year}`;
        case DateFormat.FullTime:
            const time = formatters[DateFormatter.Time].format(date);
            return `${day}.${month}.${year}, ${time}`;
        case DateFormat.FullShortYear: {
            const shortYear = formatters[DateFormatter.ShortYear].format(date);
            return `${day}.${month}.${shortYear}`;
        }
        case DateFormat.Weekday:
            return weekday;
        case DateFormat.WeekdayDayMonth:
            return `${weekday} ${day}.${month}.`;
        case DateFormat.WeekdayCommaDayMonth:
            return `${weekday}, ${day}.${month}.`;
        case DateFormat.WeekdayDayMonthYear:
            return `${weekday}, ${day}.${month}.${year}`;
        case DateFormat.WeekdayDayMonthShortYear: {
            const shortYear = formatters[DateFormatter.ShortYear].format(date);
            return `${weekday}, ${day}.${month}.${shortYear}`;
        }
        case DateFormat.WeekdayDayMonthWithoutYear: {
            return `${weekday}, ${day}.${month}.`;
        }
        case DateFormat.WeekdayDayLongMonthLongYear: {
            const longWeekDay =
                formatters[DateFormatter.LongWeekday].format(date);
            const longMonth = formatters[DateFormatter.LongMonth].format(date);
            return `${longWeekDay}, den ${day}. ${longMonth} ${year}`;
        }
        case DateFormat.SpsExpiryDate:
            return `${day}/${month}/${year}`;
        case DateFormat.LongMonthYear:
            const longMonth = formatters[DateFormatter.LongMonth].format(date);
            return `${longMonth} ${year}`;
        case DateFormat.FormatYMD:
            return `${year}-${month}-${day}`;
        case DateFormat.HourMinute:
            const hourMinute =
                formatters[DateFormatter.HourMinute].format(date);
            return `${hourMinute}`;
        default:
            // this would throw a type error in compile time
            // if we missed a case
            return TypeUtils.assertNever(format);
    }
}

/**
 * turns a date-string into the notation `DD.MM.`
 * Could be buggy, as it is adapted from the `parseDate`
 * function in `public/js/shared/datesCompare.js`
 * which is quite complex, unnecessarily so
 *
 * @param dateString {string}
 * @param format {string | undefined} default 'd.m.'
 *
 * @returns {string}
 */
const formatDate = (
    dateString: string,
    format: DateFormat = DateFormat.Short
): string => {
    if (dateString === '') {
        return '';
    }

    try {
        return formatDateObject(new Date(dateString), format);
    } catch (e) {
        console.log(e);
        return '';
    }
};

/**
 * @param minutes {number}
 * @param forceSign {boolean | undefined}
 * @param forceHourPadding {boolean | undefined}
 */
const formatMinutes = (
    minutes: number,
    forceSign?: boolean,
    forceHourPadding?: boolean
) => {
    forceSign = forceSign || false;
    const absMinutes = Math.abs(minutes);
    const sign = minutes < 0 ? '-' : forceSign ? '+' : '';
    const hours = Math.floor(absMinutes / 60);
    const minuteText = (absMinutes % 60).toString().padStart(2, '0');
    const hoursText = forceHourPadding ? String(hours).padStart(2, '0') : hours;
    return `${sign}${hoursText}:${minuteText}`;
};

/**
 * parses a given string of expected format YYYY-MM-DD or DD.MM.YYYY and returns
 * a Date object
 * @param dateString {string} expects format YYYY-MM-DD or DD.MM.YYYY
 *
 * @returns {Date}
 */
const parseDateString = (dateString: string) => {
    let dateParts: string[] = [];
    // DD.MM.YYYY
    if (/\d{2}.\d{2}.\d{4}/.test(dateString)) {
        dateParts = dateString.split('.').reverse();
    }

    // YYYY-MM-DD
    if (/\d{4}-\d{2}-\d{2}/.test(dateString)) {
        dateParts = dateString.split('-');
    }
    const [yearString, monthString, dayString] = dateParts;
    const year = parseInt(yearString, 10);
    const month = parseInt(monthString, 10) - 1;
    const day = parseInt(dayString, 10);
    const date = new Date(year, month, day);
    return date;
};

/**
 *
 * @param jsonDate W3C/JSON date string
 */
function fromJSONDate(jsonDate: string) {
    if (!jsonDate) {
        return '';
    }
    const [day, month = '', year = ''] = new Date(jsonDate)
        .toLocaleDateString('de-DE', {
            timeZone: 'Europe/Berlin',
        })
        .split('.');
    return `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}`;
}

/**
 *
 * @param dateString DD.MM.YYYY | YYYY-MM-DD => DD.MM.YYYY
 */
function toJSONDate(dateString: string): string | null {
    let dayString, monthString, yearString;
    // check for YYYY-MM-DD
    if (dateString.includes('-')) {
        [yearString = '', monthString = '', dayString = ''] =
            dateString.split('-');
    } else {
        [dayString = '', monthString = '', yearString = ''] =
            dateString.split('.');
    }
    const day = parseInt(dayString, 10);
    const month = parseInt(monthString, 10);
    const year = parseInt(yearString, 10);
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.toJSON();
}

function getAge(birthdate: Date, referenceDate: Date = new Date()) {
    const age = referenceDate.getFullYear() - birthdate.getFullYear();
    const month = referenceDate.getMonth() - birthdate.getMonth();
    if (
        month < 0 ||
        (month === 0 && referenceDate.getDate() < birthdate.getDate())
    ) {
        return age - 1;
    }
    return age;
}

function dateToString(value: Date) {
    const year = value.getFullYear();
    const month = (value.getMonth() + 1).toString().padStart(2, '0');
    const date = value.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${date}`;
}

/**
 *
 * @param dateString
 */
const getWeekday = (dateString: string, suffix: string): string => {
    if (dateString === '') {
        return '';
    }
    const date = new Date(dateString);
    return formatDateObject(date, DateFormat.Weekday) + suffix;
};

const getDateDiff = (laterDate: Date, earlierDate: Date) =>
    (laterDate.getTime() - earlierDate.getTime()) / 86400000;

const getDaysDiff = (laterDate: Date, earlierDate: Date) =>
    getDateDiff(laterDate, earlierDate) + 1;

const getHoursDiff = (laterDate: Date, earlierDate: Date) => {
    const msInHour = 1000 * 60 * 60;

    return Math.round(
        Math.abs(earlierDate.getTime() - laterDate.getTime()) / msInHour
    );
};

const getMinDiff = (laterDate: Date, earlierDate: Date) => {
    const msInMinute = 1000 * 60;

    return Math.round(
        Math.abs(earlierDate.getTime() - laterDate.getTime()) / msInMinute
    );
};

const formatDistance = (questionDate: string) => {
    const dateDiffs = DateUtils.getDaysDiff(new Date(), new Date(questionDate));
    const hoursDiff = DateUtils.getHoursDiff(
        new Date(),
        new Date(questionDate)
    );
    const minDiff = DateUtils.getMinDiff(new Date(), new Date(questionDate));

    if (hoursDiff < 24) {
        // vor hoursDiff Stunden
        if (hoursDiff === 0) {
            return minDiff > 1 ? `vor ${minDiff} Minuten` : 'gerade eben';
        }

        return hoursDiff > 1
            ? `vor ${hoursDiff} Stunden`
            : `vor ${hoursDiff} Stunde`;
    }
    if (dateDiffs > 1 && dateDiffs < 14) {
        // vor dateDiffs Tagen
        return `vor ${Math.round(dateDiffs)} Tagen`;
    }

    if (dateDiffs > 14) {
        // questionDate
        return formatDate(questionDate, DateFormat.Full);
    }

    return '';
};

const cloneDate = (date: Date) => {
    return new Date(date.getTime());
};

const isSameDay = (dateLeft: Date, dateRight: Date) => {
    // dateLeft.setHours(0, 0, 0, 0);
    // dateRight.setHours(0, 0, 0, 0);
    // return dateLeft.getTime() === dateRight.getTime();
    return (
        dateLeft.getDate() === dateRight.getDate() &&
        dateLeft.getMonth() === dateRight.getMonth() &&
        dateLeft.getFullYear() === dateRight.getFullYear()
    );
};

const isSameMonth = (dateLeft: Date, dateRight: Date) => {
    return (
        dateLeft.getFullYear() === dateRight.getFullYear() &&
        dateLeft.getMonth() === dateRight.getMonth()
    );
};

const isBefore = (date: Date, dateToCompare: Date) => {
    return date.getTime() < dateToCompare.getTime();
};

const startOfMonth = (date: Date) => {
    const clone = cloneDate(date);
    clone.setDate(1);
    clone.setHours(0, 0, 0, 0);
    return clone;
};

const isWeekend = (date: Date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
};

const isToday = (date: Date) => {
    const now = new Date();
    // now.setHours(0, 0, 0, 0);
    // date.setHours(0, 0, 0, 0);
    // return date.getTime() === now.getTime();
    return isSameDay(now, date);
    //   now.getDate() === date.getDate() &&
    //   now.getMonth() === date.getMonth() &&
    //   now.getFullYear() === date.getFullYear()
    // );
};

const isAfter = (date: Date, dateToCompare: Date) => {
    return date.getTime() > dateToCompare.getTime();
};

const startOfWeek = (date: Date) => {
    const clone = cloneDate(date);
    const weekStartsOn = 1;
    const day = clone.getDay();
    const diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;
    clone.setDate(clone.getDate() - diff);
    clone.setHours(0, 0, 0, 0);
    return clone;
};

const endOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 1, 0, 0, 0, -1);
};

const addDays = (date: Date, days: number) => {
    return new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate() + days,
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
        date.getMilliseconds()
    );
};

const addMonths = (date: Date, months: number) => {
    const clone = cloneDate(date);

    const dayOfMonth = clone.getDate();
    // The JS Date object supports date math by accepting out-of-bounds values for
    // month, day, etc. For example, new Date(2020, 1, 0) returns 31 Dec 2019 and
    // new Date(2020, 13, 1) returns 1 Feb 2021.  This is *almost* the behavior we
    // want except that dates will wrap around the end of a month, meaning that
    // new Date(2020, 13, 31) will return 3 Mar 2021 not 28 Feb 2021 as desired. So
    // we'll default to the end of the desired month by adding 1 to the desired
    // month and using a date of 0 to back up one day to the end of the desired
    // month.

    const endOfDesiredMonth = new Date(clone.getTime());
    endOfDesiredMonth.setMonth(clone.getMonth() + months + 1, 0);
    const daysInMonth = endOfDesiredMonth.getDate();

    if (dayOfMonth >= daysInMonth) {
        // If we're already at the end of the month, then this is the correct date
        // and we're done.
        return endOfDesiredMonth;
    } else {
        // Otherwise, we now know that setting the original day-of-month value won't
        // cause an overflow, so set the desired day-of-month. Note that we can't
        // just set the date of `endOfDesiredMonth` because that object may have had
        // its time changed in the unusual case where where a DST transition was on
        // the last day of the month and its local time was in the hour skipped or
        // repeated next to a DST transition.  So we use `date` instead which is
        // guaranteed to still have the original time.
        clone.setFullYear(
            endOfDesiredMonth.getFullYear(),
            endOfDesiredMonth.getMonth(),
            dayOfMonth
        );
        return clone;
    }
};

const addYears = (date: Date, amount: number) => {
    return addMonths(date, amount * 12);
};

const isWithinInterval = (date: Date, interval: { start: Date; end: Date }) => {
    const time = date.getTime();
    const startTime = interval.start.getTime();
    const endTime = interval.end.getTime();

    if (!(startTime <= endTime)) {
        throw new RangeError('Invalid interval');
    }

    return time >= startTime && time <= endTime;
};

const getYear = (date: Date) => {
    return date.getFullYear();
};

const getMonth = (date: Date) => {
    return date.getMonth();
};

const validateDate = (year: string, month: string, day: string) => {
    try {
        const y = parseInt(year, 10);
        const m = parseInt(month, 10);
        const d = parseInt(day, 10);
        const date = new Date(y, m - 1, d);
        if (date.getFullYear() !== y) {
            return false;
        }
        if (date.getMonth() !== m - 1) {
            return false;
        }
        if (date.getDate() !== d) {
            return false;
        }
    } catch (e) {
        if (process.env.NODE_ENV !== 'production') {
            console.log(e);
        }
        return false;
    }
    return true;
};

const isValid = (date: Date) => {
    return !isNaN(date.getTime());
};

const getGermanMonth = (date: Date) => {
    return GermanMonths[date.getMonth()];
};

const DateUtils = {
    formatDateObject,
    formatDate,
    formatMinutes,
    parseDateString,
    fromJSONDate,
    toJSONDate,
    dateToString,
    getAge,
    getWeekday,
    getWeekdays,
    getDateDiff,
    getDaysDiff,
    getHoursDiff,
    getMinDiff,
    formatDistance,
    validateDate,
    isSameDay,
    isSameMonth,
    isBefore,
    isValid,
    startOfMonth,
    endOfMonth,
    addDays,
    addMonths,
    addYears,
    isWithinInterval,
    isWeekend,
    isToday,
    isAfter,
    getYear,
    getMonth,
    startOfWeek,
    getGermanMonth,
};

export default DateUtils;
